import React from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AppConfigurationWrapper } from "./lib/app";
import { CookieConsentModal } from "./lib/components/cookie-consent-modal";
import {
  AlertDialogProvider,
  //  useMaterialUIController,
  AuthenticationProvider,
  InternationalizationProvider,
  MaintenanceModeProvider,
  MaterialUIControllerProvider,
  NotificationProvider,
} from "./lib/contexts";
import { useCookieConsent } from "./lib/hooks/useCookieConsent.hook";
import { RouterComponent } from "./routes";
import Themes, { getTheme } from "./styles/themes";

/**
 * Cookie Consent Wrapper
 * Must be inside InternationalizationProvider to access translations
 */
const CookieConsentWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { showConsentModal, closeConsentModal } = useCookieConsent();

  return (
    <>
      {children}
      <CookieConsentModal open={showConsentModal} onClose={closeConsentModal} />
    </>
  );
};

export const AppDependencies: React.FC<
  React.PropsWithChildren<{
    queryClient: QueryClient;
  }>
> = ({ children, queryClient }) => {
  // TODO: Implement dark mode
  // const [darkMode, setDarkMode] = React.useState<boolean>(
  //   window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
  // );

  // useEffect(() => {
  //   setDarkMode(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
  // }, [window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches]);

  // const themeName = getTheme(darkMode, false);

  const themeName = getTheme(false, false);

  return (
    <MaterialUIControllerProvider>
      <ThemeProvider theme={Themes[themeName]}>
        <CssBaseline />
        <InternationalizationProvider>
          <CookieConsentWrapper>
            <AlertDialogProvider>
              <NotificationProvider>
                <MaintenanceModeProvider>
                  <QueryClientProvider client={queryClient}>
                    <AppConfigurationWrapper>
                      <AuthenticationProvider>
                        {children}
                        <ReactQueryDevtools initialIsOpen={false} />
                      </AuthenticationProvider>
                    </AppConfigurationWrapper>
                  </QueryClientProvider>
                </MaintenanceModeProvider>
              </NotificationProvider>
            </AlertDialogProvider>
          </CookieConsentWrapper>
        </InternationalizationProvider>
      </ThemeProvider>
    </MaterialUIControllerProvider>
  );
};

const App: React.FC = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 0,
        staleTime: 5 * 60 * 1000,
      },
    },
  });

  return (
    <AppDependencies queryClient={queryClient}>
      <RouterComponent />
    </AppDependencies>
  );
};

export default App;
